import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  SetStateAction,
  Dispatch,
  useCallback,
  ComponentProps,
} from 'react';
import styled from 'styled-components';
import { useAppContext } from 'containers/AppProvider';
import EllipsisText from 'components/EllipsisText';
import DatePicker from 'components/DatePicker';
import useExportToExcel from './useExportToExcel';
import {
  Grid,
  Chip,
  TextField,
  ButtonGroup,
  Button,
  TableCell,
  Tooltip,
  Checkbox,
} from '@mui/material';
import noop from 'lodash/noop';
import useTeams from './useTeams';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useRequestStatus } from 'RequestStatusModule/hooks/useRequestStatus';
import { fetchReportsTeams } from 'thunk';
import { useTenants } from 'TenantModule/hooks/useTenants';
import { selectReportsTeamsData, selectTagsList } from 'selectors';
import TeamsReportsTable from 'ReportsModule/components/TeamsReports/TeamsReportsTable';
import useAppNavigation from 'hooks/useAppNavigation';

import { setFilters } from 'actionCreators';
import { GenericTeamInterface, HeadCell, CellRenderer, OnSuccessV2 } from 'types';
import {
  REPORTS_VIEW_TYPES,
  REPORTS_VIEW_TYPES_ARRAY,
  REMOVE_TAG_TEXT,
  REPORTS_VIEW_TYPES_HASH,
  KEYBOARD_KEYS,
} from '@constants';
import { openUrl, falsyCheckDate } from 'utils';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { format, sub } from 'date-fns';
import { TeamsHistoricalUtilization } from 'ReportsModule/types';
import useBulkActionsSelector from 'hooks/useBulkActionsSelector';
import useDeactivateTeams from 'hooks/useDeactivateTeams';
import useReactivateTeamsConfirmModal from './useReactivateTeamsConfirmModal';
import useCountStatisticalDashboard from 'hooks/useCountStatisticalDashboard';
import { useParams } from 'react-router-dom';
import { ValueOf } from 'type-fest';
import { useCsmAssignments } from 'CsmModule/hooks/useCsmAssignments';
import { FetchTeamsSuccessResponse } from 'TeamModule/types';
import { InactiveTeamsContainer } from 'ReportsModule/components/InactiveTeams/InactiveTeamsContainer';
import { useTeamConfigsQuery } from 'TeamModule/query/hooks/useTeamConfigsQuery';
import { convertSubphaseStatusConfigs } from 'TeamModule/constants/teamConfigs';
import { ToggleOptionsButton } from 'components/ToggleOptionsButton';
import useSandboxes from 'SandboxModule/hooks/useSandboxes';

const SubphaseStatusCell = ({ teamId }: CellProps) => {
  const { data } = useTeamConfigsQuery({ teamId });
  const convertedSubphaseStatus = data?.settings?.convertSubphaseStatus;

  return (
    <TableCell>
      {convertedSubphaseStatus
        ? convertSubphaseStatusConfigs[convertedSubphaseStatus]?.label
        : 'No integration'}
    </TableCell>
  );
};

const renderFirstTwoTags = (tags: Array<string>) => {
  return (
    <ChipContainer>
      {tags.slice(0, 2).map((tag) => (
        <Chip
          key={tag}
          className={tag === REMOVE_TAG_TEXT ? 'chip red-chip' : 'chip'}
          label={tag}
          color="primary"
        />
      ))}
      {tags.length >= 3 && <Chip className="chip" label={`+${tags.slice(2).length}`} />}
    </ChipContainer>
  );
};

const renderRemainingTags = (tags: Array<string>) => {
  return tags && tags.length >= 3 ? (
    <ChipContainer className="tooltip-chip-container">
      <Grid>
        {tags.slice(2).map((tag) => (
          <Chip
            key={tag}
            className={tag === REMOVE_TAG_TEXT ? 'tooltip-chip red-chip' : 'tooltip-chip'}
            label={tag}
            color="primary"
          />
        ))}
      </Grid>
    </ChipContainer>
  ) : (
    <></>
  );
};

const getMenuItems = ({
  setSelectedTeamIdsSet,
  isRequesting,
  reportTableView,
  handleOpenDeactivateTeamsModal,
  triggerInitialFetch,
  handleOpenReactivateTeamsModal,
  selectedTeamIds,
}: {
  setSelectedTeamIdsSet: Dispatch<SetStateAction<Set<number>>>;
  isRequesting: boolean;
  reportTableView: ValueOf<typeof REPORTS_VIEW_TYPES>;
  handleOpenDeactivateTeamsModal: (args: { teamIds: number[]; onSuccess: OnSuccessV2 }) => void;
  triggerInitialFetch: () => void;
  handleOpenReactivateTeamsModal: (args: { teamIds: number[]; onSuccess: OnSuccessV2 }) => void;
  selectedTeamIds: number[];
}) => {
  const menuItems: Array<{
    label: string;
    onClick: () => void;
    disableButton: boolean;
  }> = [];

  if (REPORTS_VIEW_TYPES.TEAMS === reportTableView) {
    menuItems.push({
      label: 'Deactivate Teams',
      onClick: () => {
        handleOpenDeactivateTeamsModal({
          teamIds: selectedTeamIds,
          onSuccess: ({ response }: any) => {
            triggerInitialFetch();
            setSelectedTeamIdsSet(new Set());
          },
        });
      },
      disableButton: isRequesting || !selectedTeamIds.length,
    });
  } else if (REPORTS_VIEW_TYPES.DEACTIVATED_TEAMS === reportTableView) {
    menuItems.push({
      label: 'Reactivate Teams',
      onClick: () => {
        handleOpenReactivateTeamsModal({
          teamIds: selectedTeamIds,
          onSuccess: ({ response }: any) => {
            triggerInitialFetch();
            setSelectedTeamIdsSet(new Set());
          },
        });
      },
      disableButton: isRequesting || !selectedTeamIds.length,
    });
  }

  menuItems.push({
    label: 'Clear selected teams',
    onClick: () => setSelectedTeamIdsSet(new Set()),
    disableButton: isRequesting,
  });

  return menuItems;
};

const ChipContainer = styled.span`
  .MuiChip-root {
    margin-right: 0.5rem;
    margin-top: 0.3rem;
    margin-bottom: 0.5rem;
    height: 26px;
  }
  &.tooltip-chip-container {
    flex-direction: column;
  }
  .chip {
    margin: 0 3px;
    height: 23px;
  }
  .tooltip-chip {
    margin: 4px;
    max-width: 280px;
  }
  .red-chip {
    background-color: #f44336;
    color: white;
  }
`;

const SearchButton = styled(Button)`
  margin-bottom: 10px;
  height: 2rem;
`;

const chipWidth = '200px';

type CellProps = {
  data: GenericTeamInterface;
  headCellId: string;
  teamId: number;
  teamsUtilization: TeamsHistoricalUtilization;
  handleLinkClick: (url: string) => React.MouseEventHandler;
  csmAssignmentByTeam: ReturnType<typeof useCsmAssignments>['csmAssignmentByTeam'];
};

const serializeUtilizationHeadCellId = (n: number) =>
  format(sub(new Date(), { months: n }), 'MMM,yyyy');

const deserializeUtilizationHeadCellId = (headCellId: string) => {
  const [month, year] = headCellId.split(',');
  return {
    month,
    year,
  };
};

const defaultSelectedTeamIdsSet = new Set<number>();

const exportTeamTableRequestStatusId = 'exportTeamTableRequestStatusId';

const useReports = () => {
  const { navigateToTeamProfile, navigateToTeamsTable } = useAppNavigation();
  const dispatch = useAppDispatch();
  const { reportTableView = REPORTS_VIEW_TYPES.TEAMS } = useParams<{ reportTableView?: string }>();
  const tagsList = useAppSelector(selectTagsList);
  const chipIncludeInputRef = useRef(document.createElement('input'));
  const chipExcludeInputRef = useRef(document.createElement('input'));
  const [tags, setChipList] = useState<Array<Record<string, string>>>(tagsList);
  const [excludeTags, setExcludeTagList] = useState<Array<Record<string, string>>>([]);
  const { csmAssignmentByTeam } = useCsmAssignments();
  const { exportToExcel } = useExportToExcel();
  const { shouldShowSubphaseConversionStatus: showSubphaseConversionStatus } = useFeatureFlags();
  const testTeamTags = ['dev-test', 'be-test', 'fe-test', 'intg-test', 'csm-test', 'security-test'];
  const tagsToExclude = [...testTeamTags, ...excludeTags.map((tag) => tag.value)];

  const {
    data: teams,
    allTeams,
    isRequesting: isFetchingTeams,
    error,
    filter: teamsOverviewFilter,
    totalSoFar: teamsOverviewTotalSoFar,
  } = useAppSelector(selectReportsTeamsData);
  const { searchBarInputRef } = useAppContext();
  const { isSearchMode } = teamsOverviewFilter; // Shared by all views
  const isRequesting = isFetchingTeams;
  const { DeactivateTeamsConfirmModal, handleOpenDeactivateTeamsModal } = useDeactivateTeams();
  const { fetchAllTeams } = useTeams({ shouldAutoLoad: false });
  const { ReactivateTeamsConfirmModal, handleOpenReactivateTeamsModal } =
    useReactivateTeamsConfirmModal();
  const [selectedTeamIdsSet, setSelectedTeamIdsSet] = useState(defaultSelectedTeamIdsSet);

  const selectedTeamIds = Array.from(selectedTeamIdsSet);
  const isInactiveTableView = reportTableView === REPORTS_VIEW_TYPES.INACTIVE_TEAMS;
  const disableSelectAll = selectedTeamIdsSet.size === allTeams.length || isInactiveTableView;
  const { selectedGlobalTenant } = useTenants();
  const { getSelectedRailsSandbox } = useSandboxes();
  const tenantName = selectedGlobalTenant?.tenantName || '';
  const currentSelectedRealm = getSelectedRailsSandbox();
  const currentSelectedRealmTeamName = currentSelectedRealm?.team_name;
  const currentSelectedRealmId = currentSelectedRealm?.realm_id;

  const [dateRangePreset, setDateRangePreset] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{
    startDate: string | null | undefined;
    endDate: string | null | undefined;
  }>({
    startDate: '',
    endDate: '',
  });

  const dateRangeOptions = [
    { label: 'All Time', value: 'all' },
    { label: 'Last 7 Days', value: 'week' },
    { label: 'Last 30 Days', value: 'month' },
    { label: 'Last 90 Days', value: 'quarter' },
    { label: 'Custom', value: 'custom' },
  ];

  const handleDateRangePresetChange = (value: string) => {
    setDateRangePreset(value);
    const today = new Date();

    switch (value) {
      case 'week':
        setDateRange({
          startDate: format(sub(today, { days: 7 }), 'yyyy-MM-dd'),
          endDate: format(today, 'yyyy-MM-dd'),
        });
        break;
      case 'month':
        setDateRange({
          startDate: format(sub(today, { days: 30 }), 'yyyy-MM-dd'),
          endDate: format(today, 'yyyy-MM-dd'),
        });
        break;
      case 'quarter':
        setDateRange({
          startDate: format(sub(today, { days: 90 }), 'yyyy-MM-dd'),
          endDate: format(today, 'yyyy-MM-dd'),
        });
        break;
      case 'all':
        setDateRange({
          startDate: '',
          endDate: '',
        });
        break;
      case 'custom':
        // Keep current custom dates
        break;
    }
  };

  const onCustomDateChange = (name: 'startDate' | 'endDate') => (newDate?: string | null) => {
    setDateRangePreset('custom'); // Switch to custom when manually changing dates
    setDateRange((prev) => ({
      ...prev,
      [name]: newDate ?? '',
    }));
  };

  const clearFitering = () => {
    setChipList([]);
    setSelectedTeamIdsSet(new Set());
    dispatch(
      setFilters({
        filterId: teamsOverviewFilter.id,
        isSearchMode: false,
        search_text: '',
        ids: [],
        tags: [],
      }),
    );
  };

  const selectAllTeams = () => {
    setSelectedTeamIdsSet((prev) => {
      const newSet = new Set(prev);
      allTeams.forEach((team) => {
        newSet.add(team.id);
      });
      return newSet;
    });
  };

  const defaultCellRenderer = useMemo(
    () => ({
      subphase_status: SubphaseStatusCell,
      name: ({ data }: CellProps) => (
        <TableCell>
          <EllipsisText text={data?.name || ''} width={'250px'}>
            {data?.name}
          </EllipsisText>
        </TableCell>
      ),
      checkbox: ({ teamId }: CellProps) => (
        <TableCell>
          <Checkbox
            checked={selectedTeamIdsSet.has(teamId)}
            onClick={(e) => {
              e.preventDefault();
              setSelectedTeamIdsSet((prev) => {
                const isChecked = prev.has(teamId);
                if (isChecked) {
                  prev.delete(teamId);
                } else {
                  prev.add(teamId);
                }

                return new Set(prev);
              });
            }}
          />
        </TableCell>
      ),
      team_id: ({ teamId }: CellProps) => <TableCell>{teamId}</TableCell>,
      created_at: ({ data }: CellProps) => (
        <TableCell> {falsyCheckDate(data.created_at as string)}</TableCell>
      ),
      last_user_activity_at: ({ data }: CellProps) => (
        <TableCell> {falsyCheckDate(data.last_user_activity_at as string)}</TableCell>
      ),
      tags: ({ data }: CellProps) => (
        <TableCell align="left">
          <Tooltip placement="top-start" title={renderRemainingTags(data.tags || [])}>
            {renderFirstTwoTags(data.tags || [])}
          </Tooltip>
        </TableCell>
      ),
      admins_count: ({ data }: CellProps) => <TableCell>{data.admins_count}</TableCell>,
      financial_managers_count: ({ data }: CellProps) => (
        <TableCell>{data.financial_managers_count}</TableCell>
      ),
      workload_planners_count: ({ data }: CellProps) => (
        <TableCell>{data.workload_planners_count}</TableCell>
      ),
      csm: ({ data, csmAssignmentByTeam }: CellProps) => {
        const { id: teamId } = data;
        const csmName = csmAssignmentByTeam[teamId]?.account_name || '';

        return (
          <TableCell align="left">
            <EllipsisText text={csmName} className="csm-name">
              {csmName}
            </EllipsisText>
          </TableCell>
        );
      },
    }),
    [selectedTeamIdsSet],
  );

  const tablePropsByView = {
    [REPORTS_VIEW_TYPES.TEAMS]: {
      headCells: [
        {
          id: 'checkbox',
          disablePadding: true,
          disableSort: true,
          label: '',
        },
        {
          id: 'team_id',
          disablePadding: true,
          label: 'ID',
          style: { width: 70, paddingLeft: '1rem' },
        },
        { id: 'name', numeric: true, label: 'Name', style: { width: 100 } },
        ...(showSubphaseConversionStatus
          ? [
              {
                id: 'subphase_status',
                label: 'Subphase Conversion Status',
                disableSort: true,
                style: { width: 70, textWrap: 'wrap' },
              },
            ]
          : []),
        { id: 'csm', label: 'CSM', disableSort: true },
        {
          id: 'created_at',
          label: 'Created',
        },
        {
          id: 'last_user_activity_at',
          label: 'Last Activity',
        },
        {
          id: 'actives_count',
          label: 'Active Members',
          disableSort: true,
        },
        {
          id: 'admins_count',
          label: 'Admins',
          disableSort: true,
        },
        {
          id: 'financial_managers_count',
          label: 'Budget Managers',
          disableSort: true,
        },
        {
          id: 'workload_planners_count',
          label: 'Work Planners',
          disableSort: true,
        },
        {
          id: 'projects_count',
          label: 'Projects',
          disableSort: true,
        },
        {
          id: 'project_tasks_count',

          label: 'Tasks',
          disableSort: true,
        },
        {
          id: 'time_entries_count',

          label: 'Time Entries',
          disableSort: true,
        },
        {
          id: 'created_members_count',
          label: 'Members Created',
          disableSort: true,
        },
        {
          id: 'tags',
          disablePadding: true,
          label: 'Tags',
          align: 'center',
          disableSort: true,
        },
        {
          id: 'recurly_id',
          label: 'Recurly ID',
          disableSort: true,
        },
      ],
      cellRenderer: defaultCellRenderer,
      flatList: teams.map((team) => ({
        ...team,
        selected: selectedTeamIdsSet.has(team.id),
      })),
      filter: teamsOverviewFilter,
      triggerInitialFetch: () => {
        dispatch(
          fetchReportsTeams({
            ...filter,
            isInitialFetch: true,
            isSearchMode,
            show_only_deactivated: false,
            show_deactivation_info: false,
          } as any),
        );
      },
      handleChangePage: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => {
        const newOffset = newPage * filter.limit;

        if (teamsOverviewTotalSoFar <= newOffset) {
          dispatch(
            fetchReportsTeams({
              ...filter,
              isSearchMode,
              page: newPage,
              offset: newOffset,
            } as any),
          );
        } else {
          dispatch(
            setFilters({
              filterId: filter.id,
              offset: newOffset,
              page: newPage,
            }),
          );
        }
      },
      handleRequestSort: (event: any, property: string) => {
        const { orderBy, orderDirection } = filter;
        const isAsc = orderBy === property && orderDirection === 'asc';
        const newOrderDirection = isAsc ? 'desc' : 'asc';

        setSelectedTeamIdsSet(new Set());

        dispatch(
          fetchReportsTeams({
            ...filter,
            orderBy: property,
            orderDirection: newOrderDirection,
            offset: 0,
            page: 0,
            isInitialFetch: true,
            isSearchMode,
          } as any),
        );
      },
    },
    [REPORTS_VIEW_TYPES.DEACTIVATED_TEAMS]: {
      headCells: [
        {
          id: 'checkbox',
          disablePadding: true,
          disableSort: true,
          label: '',
        },
        {
          id: 'team_id',
          disablePadding: true,
          label: 'ID',
          style: { width: 70, paddingLeft: '1rem' },
        },
        { id: 'name', numeric: true, label: 'Name' },
        { id: 'csm', label: 'CSM' },
        {
          id: 'deactivated_at',
          disableSort: true,
          label: 'Deactivated',
        },
        {
          id: 'deactivated_by_name',
          disableSort: true,
          label: 'Deactivated by',
        },
        {
          id: 'created_at',
          label: 'Created',
        },
        {
          id: 'last_user_activity_at',
          label: 'Last Activity',
        },
        {
          id: 'actives_count',
          label: 'Active Members',
          disableSort: true,
        },
        {
          id: 'admins_count',
          label: 'Admins',
          disableSort: true,
        },
        {
          id: 'financial_managers_count',
          label: 'Budget Managers',
          disableSort: true,
        },
        {
          id: 'workload_planners_count',
          label: 'Work Planners',
          disableSort: true,
        },
        {
          id: 'projects_count',
          label: 'Projects',
          disableSort: true,
        },
        {
          id: 'project_tasks_count',
          label: 'Tasks',
          disableSort: true,
        },
        {
          id: 'time_entries_count',

          label: 'Time Entries',
          disableSort: true,
        },
        {
          id: 'tags',
          disablePadding: true,
          label: 'Tags',
          align: 'center',
          disableSort: true,
        },
        {
          id: 'recurly_id',
          label: 'Recurly ID',
          disableSort: true,
        },
      ],
      cellRenderer: {
        ...defaultCellRenderer,
        deactivated_by_name: ({ data }: CellProps) => (
          <TableCell> {data.deactivated_by_name}</TableCell>
        ),
        deactivated_at: ({ data }: CellProps) => (
          <TableCell>{falsyCheckDate(data.deactivated_at)}</TableCell>
        ),
      },
      flatList: teams.map((team) => ({
        ...team,
        selected: selectedTeamIdsSet.has(team.id),
      })),
      filter: teamsOverviewFilter,
      triggerInitialFetch: () => {
        dispatch(
          fetchReportsTeams({
            ...filter,
            isInitialFetch: true,
            isSearchMode,
            show_only_deactivated: true,
            show_deactivation_info: true,
          } as any),
        );
      },
      handleChangePage: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => {
        const newOffset = newPage * filter.limit;

        if (teamsOverviewTotalSoFar <= newOffset) {
          dispatch(
            fetchReportsTeams({
              ...filter,
              isSearchMode,
              page: newPage,
              offset: newOffset,
              show_only_deactivated: true,
              show_deactivation_info: true,
            } as any),
          );
        } else {
          dispatch(
            setFilters({
              filterId: filter.id,
              offset: newOffset,
              page: newPage,
            }),
          );
        }
      },
      handleRequestSort: (event: any, property: string) => {
        const { orderBy, orderDirection } = filter;
        const isAsc = orderBy === property && orderDirection === 'asc';
        const newOrderDirection = isAsc ? 'desc' : 'asc';

        setSelectedTeamIdsSet(new Set());

        dispatch(
          fetchReportsTeams({
            ...filter,
            orderBy: property,
            orderDirection: newOrderDirection,
            offset: 0,
            page: 0,
            isInitialFetch: true,
            isSearchMode,
          } as any),
        );
      },
    },
  };

  const {
    cellRenderer,
    headCells,
    flatList,
    triggerInitialFetch = noop,
    handleChangePage,
    handleRequestSort,
    filter,
  } = tablePropsByView[reportTableView] || {};

  const { BulkActionsSelector: BulkTeamActionsSelector } = useBulkActionsSelector({
    selectedItemsIds: selectedTeamIds,
    menuItems: getMenuItems({
      setSelectedTeamIdsSet,
      isRequesting,
      reportTableView,
      handleOpenDeactivateTeamsModal,
      triggerInitialFetch,
      handleOpenReactivateTeamsModal,
      selectedTeamIds,
    }),
    anchorElProps: {
      isDisabled: isRequesting || !selectedTeamIdsSet.size,
    },
  });

  const resetSearch = () => {
    setSelectedTeamIdsSet(new Set());
    setExcludeTagList([]);
    setChipList([]);
    setDateRange({
      startDate: '',
      endDate: '',
    });
    if (searchBarInputRef.current) {
      searchBarInputRef.current.value = '';
    }

    if (reportTableView === REPORTS_VIEW_TYPES.COUNT_STATISTICS) {
      navigateToTeamsTable(REPORTS_VIEW_TYPES.TEAMS);
    } else {
      dispatch(
        fetchReportsTeams({
          ...teamsOverviewFilter,
          isInitialFetch: true,
          isSearchMode: false,
          search_text: '',
          ids: [],
          tags: [],
          excluding_tags: [],
        }),
      );
    }
  };

  /* ---------------------------- Count Statistics ---------------------------- */
  const {
    Dashboard,
    triggerCountStatisticsFilter,
    dashboardFilters,
    onSelectSearchRangeDate,
    onSelectTeamCreationDate,
    onSelectCustomSearchRange,
    selectedSearchDateRange,
    teamCreationDateOption,
    isFetchingCountStatistic,
    customEnd,
    customStart,
    tempCustomEnd,
    tempCustomStart,
  } = useCountStatisticalDashboard({ teamTags: tags.map((tag) => tag.value) });

  useEffect(() => {
    if (!REPORTS_VIEW_TYPES_HASH[reportTableView]) {
      navigateToTeamsTable();
      return;
    }

    if (!isRequesting) {
      if (reportTableView === REPORTS_VIEW_TYPES.COUNT_STATISTICS) {
        triggerCountStatisticsFilter({});
      } else {
        triggerInitialFetch();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportTableView]);

  useEffect(() => {
    if (isSearchMode) {
      setSelectedTeamIdsSet(new Set());
    }
  }, [isSearchMode]);

  /** Tags */

  const handleTagDelete = (listType: string, value: string) => () => {
    if (listType === 'include') {
      setChipList((prev) => {
        const nextTagsState = prev.filter((chip) => chip.value !== value);

        return nextTagsState;
      });
    } else {
      setExcludeTagList((prev) => {
        const nextTagsState = prev.filter((chip) => chip.value !== value);
        return nextTagsState;
      });
    }
  };

  const handleAddTag = (listType: string, value: string) => {
    if (listType === 'include') {
      setChipList((prev) => {
        if (prev.some((chip) => chip.value === value)) return prev;

        const nextTagsState = [
          ...prev,
          {
            label: value,
            value,
            type: 'include',
          },
        ];
        return nextTagsState;
      });
      chipIncludeInputRef.current.value = '';
    } else {
      setExcludeTagList((prev) => {
        const nextTagsState = [...prev];
        if (nextTagsState.some((chip) => chip.value === value)) return nextTagsState;

        nextTagsState.push({
          label: value,
          value,
          type: 'exclude',
        });
        return nextTagsState;
      });
      chipExcludeInputRef.current.value = '';
    }
  };

  const SearchTeam = () => {
    const tagValues = tags.map((tag) => tag.value);
    if (reportTableView !== REPORTS_VIEW_TYPES.COUNT_STATISTICS) {
      dispatch(
        fetchReportsTeams({
          ...teamsOverviewFilter,
          isInitialFetch: true,
          isSearchMode:
            tags.length > 0 || !!filter.search_text || !!dateRange.startDate || !!dateRange.endDate,
          tags: tagValues,
          excluding_tags: [...excludeTags.map((tag) => tag.value)],
          page: 0,
          offset: 0,
          statistics_start_date: dateRange.startDate ?? undefined,
          statistics_end_date: dateRange.endDate ?? undefined,
        } as any),
      );
    } else {
      triggerCountStatisticsFilter({ teamTags: tagValues });
      dispatch(
        setFilters({
          filterId: teamsOverviewFilter.id,
          tags: tagValues,
        }),
      );
    }
  };

  const onTagsToIncludeInputChange = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const { key } = e;
    const search = chipIncludeInputRef.current.value.trim();
    if (!search) return;
    if (key === KEYBOARD_KEYS.ENTER) {
      handleAddTag('include', search);
    }
  };

  const onTagsToExcludeInputChange = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const { key } = e;
    const search = chipExcludeInputRef.current.value.trim();
    if (!search) return;
    if (key === KEYBOARD_KEYS.ENTER) {
      handleAddTag('exclude', search);
    }
  };

  const renderChipInput = () => {
    return (
      <>
        <Grid size={5}>
          <TextField
            sx={{ width: '80%' }}
            margin="dense"
            label="Tag to include"
            disabled={isRequesting}
            type="text"
            inputRef={chipIncludeInputRef}
            onKeyDown={onTagsToIncludeInputChange}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
        </Grid>
        <Grid size={5}>
          <TextField
            sx={{ width: '80%' }}
            margin="dense"
            label="Tag to exclude"
            disabled={isRequesting}
            type="text"
            inputRef={chipExcludeInputRef}
            onKeyDown={onTagsToExcludeInputChange}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
        </Grid>
        <Grid size={2}>
          <SearchButton
            variant="contained"
            color="primary"
            onClick={SearchTeam}
            disabled={!tags && !excludeTags}
          >
            Search
          </SearchButton>
          <Button
            disabled={isRequesting}
            sx={{ display: isSearchMode ? 'block' : 'none' }}
            onClick={resetSearch}
            variant="contained"
          >
            Reset Search
          </Button>
        </Grid>
      </>
    );
  };

  const renderChip = (listType: string, chip: Record<string, string>) => {
    return (
      <ChipContainer>
        <EllipsisText text={chip.label} width={chipWidth}>
          <Chip
            color="primary"
            label={chip.label}
            disabled={isRequesting}
            onDelete={handleTagDelete(listType, chip.value)}
            sx={{ maxWidth: `calc(${chipWidth} - 10%)` }}
          />
        </EllipsisText>
      </ChipContainer>
    );
  };

  const renderDateRangeFields = () => {
    return (
      <>
        <ButtonGroup sx={{ mb: 2, flexWrap: 'wrap' }}>
          {dateRangeOptions.map((option) => (
            <Button
              key={option.value}
              variant={dateRangePreset === option.value ? 'contained' : 'outlined'}
              onClick={() => handleDateRangePresetChange(option.value)}
              sx={{
                textTransform: 'none',
                fontSize: '0.875rem',
                px: 2,
                py: 0.75,
              }}
            >
              {option.label}
            </Button>
          ))}
        </ButtonGroup>

        {dateRangePreset === 'custom' && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <DatePicker
                label="Start Date"
                value={dateRange.startDate}
                onChange={onCustomDateChange('startDate')}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <DatePicker
                label="End Date"
                value={dateRange.endDate}
                onChange={onCustomDateChange('endDate')}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  // const exportToExcel = (data: any) => {
  //   // Create a new workbook
  //   const workbook = XLSX.utils.book_new();

  //   // Convert the object data to a worksheet
  //   const worksheet = XLSX.utils.json_to_sheet(data);

  //   // Add the worksheet to the workbook
  //   XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  //   // Generate an Excel file as an array buffer
  //   const excelBuffer = XLSX.write(workbook, {
  //     type: "array",
  //     bookType: "xlsx",
  //   });

  //   // Convert the array buffer to a Blob
  //   const excelBlob = new Blob([excelBuffer], {
  //     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //   });

  //   // Save the file using file-saver library
  //   if (dateRange.startDate && dateRange.endDate) {
  //     saveAs(
  //       excelBlob,
  //       `team_data_${dateRange.startDate}-${dateRange.endDate}.xlsx`
  //     );
  //   }
  //   saveAs(excelBlob, `team_data.xlsx`);
  // };

  const { requestStatus: exportTeamTableRequestStatus, abortRequest: abortExportingTeams } =
    useRequestStatus({
      requestStatusId: exportTeamTableRequestStatusId,
      clearRequestStatusOnUnmount: false,
    });
  const isExportingTeams = exportTeamTableRequestStatus?.isRequesting;

  const handleExportClick = () => {
    fetchAllTeams({
      skipCsmFetch: true,
      include_personal: false,
      requestStatusId: exportTeamTableRequestStatusId,
      statistics_start_date: dateRange.startDate || undefined,
      statistics_end_date: dateRange.endDate || undefined,
      show_only_deactivated: false,
      show_deactivation_info: false,
      is_active: false,
      isTakeLatest: true,
      tags: tags.map((tag) => tag.value),
      excluding_tags: tagsToExclude,
      onSuccess: ({ response }: { response: { data: FetchTeamsSuccessResponse } }) => {
        const fileName = [
          'Team Data-',
          `tenant-${tenantName}`,
          currentSelectedRealmTeamName &&
            `realm-${currentSelectedRealmTeamName} (${currentSelectedRealmId})`,
          dateRange.startDate && dateRange.endDate && `${dateRange.startDate}-${dateRange.endDate}`,
        ]
          .filter(Boolean)
          .join('_');
        // Convert tags array to string
        const formattedData = response.data.teams.map((team) => ({
          ...team,
          tags: team.tags ? team.tags.join(', ') : '',
        }));
        exportToExcel({ data: formattedData, fileName });
      },
    });
  };

  /** Shared handlers */
  const handleTeamRowClick = useCallback<
    ComponentProps<typeof TeamsReportsTable>['handleTeamRowClick']
  >(
    ({ shouldOpenInAnotherTab, team }) => {
      navigateToTeamProfile({
        teamId: team.id,
        shouldOpenInAnotherTab,
      });
    },
    [navigateToTeamProfile],
  );

  const handleLinkClick = (url: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    openUrl(url);
  };

  const onViewToggle: ComponentProps<typeof ToggleOptionsButton>['onSelect'] = ({ option }) => {
    navigateToTeamsTable(option.value);
    setSelectedTeamIdsSet(new Set());
  };

  const getIsTeamSelected = useCallback(
    (teamId: number) => selectedTeamIdsSet.has(teamId),
    [selectedTeamIdsSet],
  );

  const onTeamCheck = (teamId: number) => {
    setSelectedTeamIdsSet((prev) => {
      if (prev.has(teamId)) {
        prev.delete(teamId);
      } else {
        prev.add(teamId);
      }
      return new Set(prev);
    });
  };

  const renderViewToggle = () => {
    return (
      <ToggleOptionsButton
        options={REPORTS_VIEW_TYPES_ARRAY}
        currentOption={reportTableView}
        onSelect={onViewToggle}
        isDisabled={isRequesting}
      />
    );
  };

  const renderNonTableBody = useCallback(() => {
    if (reportTableView === REPORTS_VIEW_TYPES.COUNT_STATISTICS) {
      return Dashboard;
    }
    return <></>;
  }, [Dashboard, reportTableView]);

  const renderBody = useCallback(() => {
    if (!REPORTS_VIEW_TYPES_HASH[reportTableView]) return <></>;

    const isTable = reportTableView !== REPORTS_VIEW_TYPES.COUNT_STATISTICS;

    if (isInactiveTableView) return <InactiveTeamsContainer />;

    const hidePagination = isRequesting || !isTable;

    const Body = isTable ? (
      <TeamsReportsTable
        data={flatList}
        headCells={headCells as HeadCell[]}
        teamsUtilization={[]}
        cellRenderer={cellRenderer as CellRenderer<CellProps>}
        filter={filter}
        isRequesting={isRequesting}
        useDefaultTable={isTable}
        hidePagination={hidePagination}
        csmAssignmentByTeam={csmAssignmentByTeam}
        handleLinkClick={handleLinkClick}
        handleRequestSort={handleRequestSort}
        handleTeamRowClick={handleTeamRowClick}
        handleChangePage={handleChangePage}
      />
    ) : (
      renderNonTableBody()
    );

    return Body;
  }, [
    cellRenderer,
    csmAssignmentByTeam,
    filter,
    flatList,
    getIsTeamSelected,
    handleChangePage,
    handleRequestSort,
    handleTeamRowClick,
    headCells,
    isInactiveTableView,
    isRequesting,
    renderNonTableBody,
    reportTableView,
  ]);

  return {
    handleExportClick,
    renderChip,
    renderChipInput,
    tags,
    excludeTags,
    resetSearch,
    reportTableView,
    renderBody,
    isRequesting,
    isSearchMode,
    renderViewToggle,
    BulkTeamActionsSelector,
    DeactivateTeamsConfirmModal,
    ReactivateTeamsConfirmModal,
    selectAllTeams,
    disableSelectAll,
    renderDateRangeFields,
    isExportingTeams,
    abortExportingTeams,
    statisticalCountsProps: {
      triggerCountStatisticsFilter,
      isFetchingCountStatistic,
      dashboardFilters,
      onSelectSearchRangeDate,
      onSelectTeamCreationDate,
      teamCreationDateOption,
      selectedSearchDateRange,
      customEnd,
      customStart,
      tempCustomEnd,
      tempCustomStart,
      onSelectCustomSearchRange,
    },
  };
};

export default useReports;
